import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Save } from 'lucide-react';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { TabSkeleton } from '../BotManagement/BotManagementSkeleton';
import { CHECKOUT_NAME_MAX, checkoutNameError } from '../../../utils/checkoutName';
import CheckoutAvatar from './CheckoutAvatar';

/**
 * Aba "Checkout": o que aparece na página de checkout da Spectrum (cartão do
 * Global, SPEI/Nequi/Chile do LATAM): foto, nome e número do pedido. Vale para
 * todos os mercados do bot.
 */
export default function CheckoutConfig() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();

    const [loadingPage, setLoadingPage] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasAvatar, setHasAvatar] = useState(false);
    const [name, setName] = useState('');
    const [botName, setBotName] = useState('');
    const [showOrder, setShowOrder] = useState(false);

    useEffect(() => {
        if (!botId) return;
        let alive = true;
        setLoadingPage(true);
        authService.bots.edit_bot(botId)
            .then(({ data }) => {
                if (!alive) return;
                const pc = data.bot?.pix_config || {};
                setHasAvatar(!!pc.has_checkout_avatar);
                setName(pc.checkout_name || '');
                setShowOrder(pc.checkout_show_order === true);
                setBotName(data.bot?.bot_name || data.bot?.bot_username || '');
            })
            .catch(() => alive && showError('Erro ao carregar o checkout'))
            .finally(() => alive && setLoadingPage(false));
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nameError = checkoutNameError(name);
        if (nameError) {
            showError(nameError);
            return;
        }
        setSaving(true);
        try {
            await authService.bots.save_pix_config(botId, {
                checkout_name: name.trim(),
                checkout_show_order: showOrder,
            });
            showSuccess('Checkout salvo!');
        } catch (err) {
            showError(err?.response?.data?.error || err?.message || 'Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    if (loadingPage) return <TabSkeleton tab="checkout" />;

    return (
        <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
            <div className="spt-form-bot">
                <p className="spt-hint">Vale para a página de checkout da Spectrum em todos os mercados do bot.</p>

                {/* FOTO (salva na hora, fora do "Salvar") */}
                <div className="mt-4">
                    <CheckoutAvatar botId={botId} hasAvatar={hasAvatar} />
                </div>

                <div className="mt-6">
                    <label className="spt-label" htmlFor="checkout-name">Nome no checkout</label>
                    <p className="spt-hint">Aparece ao lado da foto, no topo do checkout. Vazio = nome do bot.</p>
                    <input
                        id="checkout-name"
                        className="input3"
                        value={name}
                        maxLength={CHECKOUT_NAME_MAX}
                        placeholder={botName || 'Nome do bot'}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <p className="spt-hint mt-1">{name.trim().length}/{CHECKOUT_NAME_MAX}</p>
                </div>

                <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="spt-label mb-0">Mostrar número do pedido no checkout</h3>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                aria-label="Mostrar número do pedido no checkout"
                                checked={showOrder}
                                onChange={(e) => setShowOrder(e.target.checked)}
                            />
                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500" />
                        </label>
                    </div>
                    <p className="spt-hint">
                        Ligado: o checkout mostra "Order #123" (como a Stripe). Desligado: mostra o nome do plano.
                    </p>
                </div>

                <div className="flex justify-end mt-12">
                    <button disabled={saving} className="btn-primary">
                        {saving
                            ? <><Loader2 size={14} className="animate-spin me-2" />Salvando...</>
                            : <><Save size={14} className="me-2" />Salvar checkout</>}
                    </button>
                </div>
            </div>
        </form>
    );
}
